import { useState } from 'react';
import { api } from '../../../services/api';
import { formatPrice } from '../../../utils/format';

interface ProduitStock {
  id: number;
  nom: string;
  type: string;
  couleur: string;
  quantite_stock: number;
  unite: string;
  seuil_alerte: number;
  prix_unitaire: number;
  image_url: string;
}

interface SelectedItem {
  type_habit_id: string;
  nom: string;
  prix_unitaire: number;
  quantite: number;
  tissus: any[];
}

interface Props {
  selectedItems: SelectedItem[];
  onChange: (items: SelectedItem[]) => void;
  stock: ProduitStock[];
  isGlobal?: boolean;
  orderFabrics?: any[];
  onOrderFabricsChange?: (fabrics: any[]) => void;
}

export default function StepTissuAssignation({ selectedItems, onChange, stock, isGlobal = false, orderFabrics = [], onOrderFabricsChange }: Props) {
  const [loading, setLoading] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  const addTissu = (isClient: boolean) => {
    const defaultTissu = {
      tempId: 'tissu-' + Date.now() + '-' + Math.floor(Math.random() * 1000),
      is_client_cloth: isClient,
      description: '',
      tissu_stock_id: null,
      quantite: 0
    };

    if (isGlobal) {
      if (onOrderFabricsChange) {
        onOrderFabricsChange([...orderFabrics, defaultTissu]);
      }
    } else {
      const newItems = [...selectedItems];
      if (!newItems[activeItemIndex].tissus) newItems[activeItemIndex].tissus = [];
      newItems[activeItemIndex].tissus.push(defaultTissu);
      onChange(newItems);
    }
  };

  const updateTissu = (tissuIndex: number, field: string, value: any) => {
    if (isGlobal) {
      if (onOrderFabricsChange) {
        const newFabrics = [...orderFabrics];
        newFabrics[tissuIndex] = {
          ...newFabrics[tissuIndex],
          [field]: value
        };
        onOrderFabricsChange(newFabrics);
      }
    } else {
      const newItems = [...selectedItems];
      newItems[activeItemIndex].tissus[tissuIndex] = {
          ...newItems[activeItemIndex].tissus[tissuIndex],
          [field]: value
      };
      onChange(newItems);
    }
  };

  const removeTissu = (tissuIndex: number) => {
    if (isGlobal) {
      if (onOrderFabricsChange) {
        const newFabrics = [...orderFabrics];
        newFabrics.splice(tissuIndex, 1);
        onOrderFabricsChange(newFabrics);
      }
    } else {
      const newItems = [...selectedItems];
      newItems[activeItemIndex].tissus.splice(tissuIndex, 1);
      onChange(newItems);
    }
  };

  if (!isGlobal && selectedItems.length === 0) return <div className="p-8 text-center text-gray-500">Veuillez d'abord sélectionner des types de couture.</div>;

  const currentTissus = isGlobal ? orderFabrics : (selectedItems[activeItemIndex]?.tissus || []);

  return (
    <div className="space-y-6">
      {/* Navigation entre items si plusieurs et pas global */}
      {!isGlobal && selectedItems.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {selectedItems.map((item, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setActiveItemIndex(idx)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap border-2 ${
                        activeItemIndex === idx ? 'bg-foret-500 text-white border-foret-500 shadow-md' : 'bg-white text-gray-500 border-gray-100 hover:border-gray-200'
                    }`}
                  >
                      {item.nom} ({item.quantite})
                  </button>
              ))}
          </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-bold text-gray-900">{isGlobal ? "Tissus de la commande" : selectedItems[activeItemIndex]?.nom}</h3>
                    <p className="text-xs text-gray-500">{isGlobal ? "Déclarez les tissus de cette commande (Boutique ou Client)" : "Assignez les tissus pour cet article"}</p>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => addTissu(true)}
                        className="p-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 flex items-center gap-2 text-xs font-bold"
                    >
                        <i className="ri-user-received-line" /> + Tissu Client
                    </button>
                    <button 
                        onClick={() => addTissu(false)}
                        className="p-2 bg-terre-50 text-terre-600 rounded-lg hover:bg-terre-100 flex items-center gap-2 text-xs font-bold"
                    >
                        <i className="ri-store-2-line" /> + Tissu Boutique
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {currentTissus.length === 0 && (
                    <div className="p-10 text-center border-2 border-dashed border-gray-100 rounded-2xl">
                        <p className="text-sm text-gray-400">Aucun tissu assigné. Cliquez sur les boutons ci-dessus pour ajouter.</p>
                    </div>
                )}

                {currentTissus.map((t, tIdx) => (
                    <div key={t.tempId || tIdx} className={`p-4 rounded-2xl border-2 animate-in slide-in-from-right-4 duration-300 ${
                        t.is_client_cloth ? 'border-gray-100 bg-gray-50' : 'border-terre-100 bg-terre-50'
                    }`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                                    t.is_client_cloth ? 'bg-gray-200 text-gray-600' : 'bg-terre-500 text-white'
                                }`}>
                                    <i className={t.is_client_cloth ? 'ri-user-received-line' : 'ri-store-2-line'} />
                                </div>
                                <span className="text-xs font-bold uppercase tracking-wider">{t.is_client_cloth ? 'Tissu Client' : 'Tissu Boutique'}</span>
                            </div>
                            <button 
                                onClick={() => removeTissu(tIdx)} 
                                className="text-red-400 hover:text-red-600"
                                title="Supprimer ce tissu"
                                aria-label="Supprimer ce tissu"
                            >
                                <i className="ri-delete-bin-line" />
                            </button>
                        </div>

                        {t.is_client_cloth ? (
                            <input 
                                type="text"
                                placeholder="Description du tissu (ex: Wax bleu 3m)"
                                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-500 bg-white"
                                value={t.description}
                                onChange={e => updateTissu(tIdx, 'description', e.target.value)}
                            />
                        ) : (
                            <div className="space-y-3">
                                <select 
                                    className="w-full px-4 py-3 text-sm border border-terre-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-terre-500 bg-white font-bold"
                                    value={t.tissu_stock_id || ''}
                                    onChange={e => updateTissu(tIdx, 'tissu_stock_id', e.target.value)}
                                    title="Choisir un tissu du stock"
                                    aria-label="Sélecteur de tissu du stock"
                                >
                                    <option value="">Sélectionner un tissu...</option>
                                    {stock.map(s => (
                                        <option key={s.id} value={s.id}>
                                            {s.nom} ({s.quantite_stock} {s.unite} dispo) - {formatPrice(s.prix_unitaire)}/m
                                        </option>
                                    ))}
                                </select>
                                {t.tissu_stock_id && (
                                    <div className="flex items-center gap-3 animate-in fade-in">
                                         <div className="flex-1">
                                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Mètres achetés / alloués</label>
                                            <input 
                                                id={`qte-tissu-${tIdx}`}
                                                type="number" 
                                                step="0.1"
                                                className="w-full px-4 py-2 border border-terre-200 rounded-lg focus:ring-2 focus:ring-terre-500 outline-none font-bold"
                                                value={t.quantite}
                                                onChange={e => updateTissu(tIdx, 'quantite', e.target.value)}
                                                placeholder="Quantité en mètres"
                                                title="Mètres à utiliser"
                                                aria-label="Quantité de tissu en mètres"
                                            />
                                         </div>
                                         {t.tissu_stock_id && (
                                            <div className="pt-5">
                                                 <img 
                                                    src={api.getProxyUrl(stock.find(s => String(s.id) === String(t.tissu_stock_id))?.image_url) || ''} 
                                                    className="w-12 h-12 rounded-lg object-cover border border-terre-200" 
                                                    alt="tissu" 
                                                 />
                                            </div>
                                         )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
      </div>
    </div>
  );
}
